import { Trans } from "@lingui/react/macro"
import { useMemo } from "react"
import { resolveSpeakerColor } from "../../../../lib/speaker-colors"
import { talkPercent } from "../../helpers"
import classes from "../../styles.module.css"
import type { IProps } from "./interfaces"

/**
 * CostCard renders the analyser's estimated meeting cost in EUR with a
 * "≈ €X per person" stat and a per-speaker share when talk-time is known.
 *
 * Returns `null` when the cost estimate is missing or zero — at that point
 * the card has nothing meaningful to show. Per-speaker bar / legend is
 * suppressed when `talkShare` isn't available; the headline still renders.
 */
export function CostCard({
  costEur,
  hourlyRate,
  attendeeCount,
  speakerNames,
  talkShare,
  speakerColorMap,
}: IProps): React.JSX.Element | null {
  const talk = useMemo(() => talkPercent(speakerNames, talkShare), [speakerNames, talkShare])
  if (!costEur || costEur <= 0) return null
  const perPerson = attendeeCount > 0 ? Math.round(costEur / attendeeCount) : 0
  // Dedupe by name: `talk` already sums percentages per name, so a person who
  // occupies more than one roster slot (a merged far-end echo) renders as one
  // row, not two.
  const list = [...new Set(speakerNames)]
    .map((name) => ({ name, pct: talk.get(name) ?? 0 }))
    .filter((x) => x.pct > 0)
    .sort((a, b) => b.pct - a.pct)

  return (
    <section className={classes.card}>
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>
          <Trans>Cost split</Trans>
        </h2>
      </div>
      <div className={classes.costTop}>
        <span className={classes.costBig}>€{costEur}</span>
        <span className={classes.costPer}>
          <Trans>≈ €{perPerson} per person</Trans>
        </span>
      </div>
      {hourlyRate ? (
        <p className={classes.costNote}>
          <Trans>
            {attendeeCount} people × time × €{hourlyRate}/h
          </Trans>
        </p>
      ) : null}
      {list.length > 0 && (
        <>
          <div className={classes.costBar}>
            {list.map((p, i) => (
              <div
                key={`${i}-${p.name}`}
                className={classes.costSeg}
                style={{ width: `${p.pct}%`, background: resolveSpeakerColor(p.name, speakerColorMap) }}
              />
            ))}
          </div>
          <div className={classes.costLegend}>
            {list.map((p, i) => (
              <div key={`${i}-${p.name}`} className={classes.costLegendRow}>
                <span style={{ background: resolveSpeakerColor(p.name, speakerColorMap) }} />
                <span className="who">{p.name}</span>
                <span className="v">€{Math.round((costEur * p.pct) / 100)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}
